import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import WebIcon from "@mui/icons-material/Web";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CodeIcon from "@mui/icons-material/Code";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import JsonDataModal from "../components/admin/JsonDataModal";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { useQuery } from "@tanstack/react-query";

export default function SquarespaceWebsites() {
  const navigate = useNavigate();
  const api = useApi();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [currentPageUrl, setCurrentPageUrl] = React.useState(null);
  const [pageHistory, setPageHistory] = React.useState([]);

  const { data: responseData, isLoading, error, refetch } = useQuery({
    queryKey: ["squarespace-websites", currentPageUrl ?? "initial"],
    queryFn: async () => {
      const path = currentPageUrl
        ? `/admin/squarespace/websites?nextPageUrl=${encodeURIComponent(currentPageUrl)}`
        : "/admin/squarespace/websites";
      return api.get(path);
    },
  });

  const websitesData = responseData
    ? { data: responseData.data || [], pagination: responseData.pagination || {} }
    : null;
  const paginationUrls = {
    nextPageUrl: responseData?.nextPageUrl || responseData?.pagination?.nextPageUrl || null,
    previousPageUrl: responseData?.previousPageUrl || responseData?.pagination?.previousPageUrl || null,
  };
  const loading = isLoading;

  const [jsonModalOpen, setJsonModalOpen] = React.useState(false);
  const [selectedWebsiteJsonModalOpen, setSelectedWebsiteJsonModalOpen] = React.useState(false);
  const [selectedWebsite, setSelectedWebsite] = React.useState(null);

  const handleNextPage = () => {
    if (paginationUrls.nextPageUrl) {
      setPageHistory((prev) => [...prev, currentPageUrl]);
      setCurrentPageUrl(paginationUrls.nextPageUrl);
    }
  };

  const handlePreviousPage = () => {
    if (paginationUrls.previousPageUrl) {
      setCurrentPageUrl(paginationUrls.previousPageUrl);
    } else if (pageHistory.length > 0) {
      const previousUrl = pageHistory[pageHistory.length - 1];
      setPageHistory((prev) => prev.slice(0, -1));
      setCurrentPageUrl(previousUrl);
    } else {
      setCurrentPageUrl(null);
    }
  };

  // Extract websites from data array
  const allWebsites = Array.isArray(websitesData?.data) ? websitesData.data : [];
  
  // Filter by search query
  const websites = React.useMemo(() => {
    if (!searchQuery.trim()) return allWebsites;
    
    const query = searchQuery.toLowerCase().trim();
    return allWebsites.filter((website) => {
      const title = website.title || '';
      const domain = website.domain || website.domainName || '';
      const id = website.id || website.websiteId || '';
      const customerId = website.customerId || '';
      const searchableText = `${title} ${domain} ${id} ${customerId}`.toLowerCase();
      return searchableText.includes(query);
    });
  }, [allWebsites, searchQuery]);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Websites
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Squarespace Websites"
              description="View all Squarespace websites"
              icon={<WebIcon sx={{ color: "primary.main" }} />}
            >
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={loading}
                sx={{ mr: 1 }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<CodeIcon />}
                onClick={() => setJsonModalOpen(true)}
              >
                View JSON
              </Button>
            </SquarespacePageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.message || "Failed to fetch websites"}
              </Alert>
            )}

            {websitesData?._derivedFromOrders && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Websites are derived from completed orders (websites endpoint not available in Squarespace API)
              </Alert>
            )}

            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <TextField
                  fullWidth
                  placeholder="Search websites by title, domain, ID, or customer ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : websites.length === 0 ? (
                  <Alert severity="info">
                    {searchQuery ? "No websites match your search." : "No websites found."}
                  </Alert>
                ) : (
                  <>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Website ID</TableCell>
                            <TableCell>Sqsp Website ID</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {websites.map((website) => (
                            <TableRow key={website.id || website.websiteId} hover>
                              <TableCell>
                                <Link
                                  to={`/dashboard/squarespace/websites/${website.id || website.websiteId}`}
                                  style={{ textDecoration: "none" }}
                                >
                                  <Typography
                                    variant="body2"
                                    fontFamily="monospace"
                                    color="primary"
                                    sx={{ "&:hover": { textDecoration: "underline" } }}
                                  >
                                    {website.id || website.websiteId || "N/A"}
                                  </Typography>
                                </Link>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace">
                                  {website.sqspWebsiteId || website.sqsp_website_id || website.squarespaceWebsiteId || "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => navigate(`/dashboard/squarespace/websites/${website.id || website.websiteId}`)}
                                  >
                                    View Details
                                  </Button>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedWebsite(website);
                                      setSelectedWebsiteJsonModalOpen(true);
                                    }}
                                    aria-label="View website JSON"
                                  >
                                    <CodeIcon fontSize="small" />
                                  </IconButton>
                                  {website.customerId && (
                                    <Button
                                      size="small"
                                      startIcon={<VisibilityIcon />}
                                      onClick={() => navigate(`/dashboard/squarespace/customers/${website.customerId}`)}
                                    >
                                      View Customer
                                    </Button>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {(paginationUrls.nextPageUrl || paginationUrls.previousPageUrl || pageHistory.length > 0) && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                        <Button
                          onClick={handlePreviousPage}
                          disabled={!paginationUrls.previousPageUrl && pageHistory.length === 0}
                        >
                          Previous
                        </Button>
                        <Button
                          onClick={handleNextPage}
                          disabled={!paginationUrls.nextPageUrl}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Container>
          <Footer />
        </Box>
      </Box>

      <JsonDataModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={{
          websites: websites,
          total: websites.length,
          pagination: websitesData?.pagination,
        }}
        title="Websites JSON - All Websites"
      />
      
      <JsonDataModal
        open={selectedWebsiteJsonModalOpen}
        onClose={() => {
          setSelectedWebsiteJsonModalOpen(false);
          setSelectedWebsite(null);
        }}
        data={selectedWebsite}
        title={`Website JSON - ${selectedWebsite?.title || selectedWebsite?.id || selectedWebsite?.websiteId || "Website"}`}
      />
    </>
  );
}
